import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiX, FiGlobe, FiMapPin } from 'react-icons/fi';
import API from '../utils/api';
import TripCard from '../components/TripCard';
import './Trips.css';

const CATEGORIES = ['All', 'Adventure', 'Cultural', 'Wildlife', 'Beach', 'Mountain', 'Offbeat', 'Spiritual'];
const SORT_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'rating', label: 'Top Rated' },
];

const STATIC_TRIPS = [
  {
    _id: '1', title: 'Ladakh Adventure Trek', destination: 'Leh, Ladakh',
    description: 'Experience the raw beauty of the Himalayan high-altitude desert. Visit ancient monasteries, pristine lakes, and rugged mountain passes in this unforgettable journey.',
    duration: '7N/8D', price: 18999, originalPrice: 22000, maxGroupSize: 12, difficulty: 'Challenging', category: 'Adventure',
    coverImage: '/ladakh.jpg', rating: 4.9, reviewCount: 45, featured: true, upcoming: true, isActive: true, tripType: 'Domestic',
  },
  {
    _id: '2', title: 'Meghalaya Exploration', destination: 'Shillong & Cherrapunji',
    description: 'Discover the land of living root bridges, crystal-clear rivers, and the wettest place on Earth. A paradise for nature lovers and photographers alike.',
    duration: '5N/6D', price: 14999, originalPrice: 18000, maxGroupSize: 15, difficulty: 'Easy', category: 'Offbeat',
    coverImage: '/meghalaya.jpg', rating: 4.8, reviewCount: 62, featured: true, upcoming: true, isActive: true, tripType: 'Domestic',
  },
  {
    _id: '3', title: 'Kashmir Valley Tour', destination: 'Srinagar & Gulmarg, Kashmir',
    description: 'Float on Dal Lake in a shikara, walk through Mughal gardens, and witness the breathtaking beauty of the Valley. Paradise is closer than you think.',
    duration: '6N/7D', price: 16999, originalPrice: null, maxGroupSize: 12, difficulty: 'Easy', category: 'Cultural',
    coverImage: '/kashmir.jpg', rating: 4.9, reviewCount: 38, featured: true, upcoming: true, isActive: true, tripType: 'Domestic',
  },
  {
    _id: '4', title: 'Spiti Valley Expedition', destination: 'Spiti Valley, Himachal Pradesh',
    description: 'Journey to one of the coldest and highest inhabited places on Earth. Ancient Tibetan monasteries, barren moonscapes, and starlit skies await.',
    duration: '8N/9D', price: 19999, originalPrice: 24000, maxGroupSize: 10, difficulty: 'Challenging', category: 'Mountain',
    coverImage: 'https://images.pexels.com/photos/4534200/pexels-photo-4534200.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
    rating: 4.8, reviewCount: 29, featured: false, upcoming: true, isActive: true, tripType: 'Domestic',
  },
  {
    _id: '5', title: 'Kerala Backwaters & Hills', destination: 'Alleppey, Munnar & Kovalam, Kerala',
    description: 'Cruise through the serene backwaters on a traditional Kerala houseboat, wake up to mist-covered tea estates in Munnar, and unwind on golden beaches.',
    duration: '5N/6D', price: 15999, originalPrice: 18500, maxGroupSize: 14, difficulty: 'Easy', category: 'Beach',
    coverImage: '/kerala.jpg', rating: 4.7, reviewCount: 53, featured: false, upcoming: true, isActive: true, tripType: 'Domestic',
  },
  {
    _id: '6', title: 'Sri Lanka Island Escape', destination: 'Colombo, Kandy & Ella, Sri Lanka',
    description: 'Explore ancient ruins, beautiful beaches, and vibrant culture. From the cultural triangle to the hill country, Sri Lanka is full of wonders.',
    duration: '6N/7D', price: 24999, originalPrice: 29000, maxGroupSize: 12, difficulty: 'Easy', category: 'Cultural',
    coverImage: '/srilanka.jpg', rating: 4.8, reviewCount: 27, featured: false, upcoming: true, isActive: true, tripType: 'International',
  },
  {
    _id: '7', title: 'Arunachal Pradesh Discovery', destination: 'Tawang, Arunachal Pradesh',
    description: "Venture to India's easternmost frontier — home to vast monasteries, tribal cultures, and untouched natural beauty rarely seen by tourists.",
    duration: '7N/8D', price: 21999, originalPrice: 25000, maxGroupSize: 10, difficulty: 'Moderate', category: 'Offbeat',
    coverImage: 'https://images.pexels.com/photos/2526935/pexels-photo-2526935.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
    rating: 4.9, reviewCount: 21, featured: false, upcoming: false, isActive: true, tripType: 'Domestic',
  },
  {
    _id: '8', title: 'Coorg Coffee Trails', destination: 'Coorg, Karnataka',
    description: 'Wander through fragrant coffee estates, trek to hidden waterfalls, and experience the hospitality of the Kodava people in this hill paradise.',
    duration: '3N/4D', price: 8999, originalPrice: 11000, maxGroupSize: 15, difficulty: 'Easy', category: 'Wildlife',
    coverImage: 'https://images.pexels.com/photos/5174013/pexels-photo-5174013.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
    rating: 4.6, reviewCount: 34, featured: false, upcoming: false, isActive: true, tripType: 'Domestic',
  },
  {
    _id: '9', title: 'Backpacking Bali', destination: 'Bali, Indonesia',
    description: "From Uluwatu's dramatic cliffs and Kecak Fire Dance to Nusa Penida's crystal waters, Tegalalang rice terraces, and Tanah Lot sunset — Bali is a paradise that captivates every sense.",
    duration: '5N/6D', price: 32999, originalPrice: 39000, maxGroupSize: 12, difficulty: 'Easy', category: 'Beach',
    coverImage: 'https://images.pexels.com/photos/3225531/pexels-photo-3225531.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
    rating: 4.9, reviewCount: 41, featured: true, upcoming: true, isActive: true, tripType: 'International',
  },
  {
    _id: '10', title: 'Backpacking Tawang', destination: 'Tawang, Arunachal Pradesh',
    description: "Journey to the Land of Lamas — Tawang's ancient monastery, the paradise lake of Sela Pass, the Indo-China border at Bum La, and the serene Sangti Valley await.",
    duration: '6N/7D', price: 18999, originalPrice: 22000, maxGroupSize: 12, difficulty: 'Moderate', category: 'Offbeat',
    coverImage: 'https://images.pexels.com/photos/2526935/pexels-photo-2526935.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
    rating: 4.9, reviewCount: 33, featured: true, upcoming: true, isActive: true, tripType: 'Domestic',
  },
];

export default function Trips() {
  const [allTrips, setAllTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Domestic'); // 'Domestic' | 'International'
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('');

  useEffect(() => {
    const fetchTrips = async () => {
      setLoading(true);
      try {
        const res = await API.get('/api/trips');
        const data = res.data.data;
        setAllTrips(data?.length > 0 ? data : STATIC_TRIPS);
      } catch {
        setAllTrips(STATIC_TRIPS);
      } finally {
        setLoading(false);
      }
    };
    fetchTrips();
  }, []);

  // Filter & sort
  const getFiltered = (type) => {
    let result = allTrips.filter(t => {
      const tType = t.tripType || (
        ['Sri Lanka Island Escape', 'Backpacking Bali'].includes(t.title)
          ? 'International' : 'Domestic'
      );
      return tType === type;
    });
    if (activeCategory !== 'All') result = result.filter(t => t.category === activeCategory);
    if (search) result = result.filter(t =>
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.destination.toLowerCase().includes(search.toLowerCase())
    );
    if (sort === 'price_asc') result.sort((a, b) => a.price - b.price);
    if (sort === 'price_desc') result.sort((a, b) => b.price - a.price);
    if (sort === 'rating') result.sort((a, b) => b.rating - a.rating);
    return result;
  };

  const domesticTrips = getFiltered('Domestic');
  const internationalTrips = getFiltered('International');
  const activeTrips = activeTab === 'Domestic' ? domesticTrips : internationalTrips;

  return (
    <div className="trips-page">
      {/* ── Hero ── */}
      <div className="trips-hero">
        <div className="trips-hero-bg" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920&h=500&fit=crop)' }} />
        <div className="trips-hero-overlay" />
        <motion.div className="trips-hero-content" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span style={{ fontFamily: 'var(--font-script)', fontSize: '1.3rem', color: 'var(--secondary-light)' }}>Handpicked For You</span>
          <h1>Explore All Trips</h1>
          <p>Discover our collection of carefully crafted offbeat journeys across India and beyond</p>
        </motion.div>
      </div>

      <div className="trips-content">
        <div className="container">

          {/* ── Domestic / International Tab Switch ── */}
          <div className="trip-type-tabs">
            <button
              className={`type-tab ${activeTab === 'Domestic' ? 'active' : ''}`}
              onClick={() => { setActiveTab('Domestic'); setActiveCategory('All'); }}
            >
              <span className="type-tab-icon"><FiMapPin /></span>
              <span className="type-tab-label">Domestic</span>
              <span className="type-tab-count">{loading ? '—' : domesticTrips.length}</span>
            </button>
            <button
              className={`type-tab ${activeTab === 'International' ? 'active' : ''}`}
              onClick={() => { setActiveTab('International'); setActiveCategory('All'); }}
            >
              <span className="type-tab-icon"><FiGlobe /></span>
              <span className="type-tab-label">International</span>
              <span className="type-tab-count">{loading ? '—' : internationalTrips.length}</span>
            </button>
          </div>

          {/* ── Section heading ── */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              className="trips-section-heading"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === 'Domestic' ? (
                <>
                  <span className="section-flag">🇮🇳</span>
                  <div>
                    <h2>Explore India</h2>
                    <p>Handpicked journeys across the incredible landscapes of India</p>
                  </div>
                </>
              ) : (
                <>
                  <span className="section-flag">🌏</span>
                  <div>
                    <h2>Beyond Borders</h2>
                    <p>International adventures crafted for the true explorer</p>
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>

          {/* ── Search + Sort ── */}
          <motion.div className="filters-bar" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="search-box">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder={`Search ${activeTab.toLowerCase()} trips...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && <button className="clear-search" onClick={() => setSearch('')}><FiX /></button>}
            </div>
            <div className="filter-controls">
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="sort-select">
                {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
          </motion.div>

          {/* ── Category pills ── */}
          <div className="categories-scroll">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                className={`category-btn ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* ── Results count ── */}
          <div className="results-info">
            <p>
              {loading ? 'Loading...' : `${activeTrips.length} trip${activeTrips.length !== 1 ? 's' : ''} found`}
              {activeCategory !== 'All' && ` in ${activeCategory}`}
            </p>
          </div>

          {/* ── Grid ── */}
          {loading ? (
            <div className="loading-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="skeleton-card">
                  <div className="skeleton-img" />
                  <div className="skeleton-body">
                    <div className="skeleton-line" />
                    <div className="skeleton-line short" />
                    <div className="skeleton-line" />
                  </div>
                </div>
              ))}
            </div>
          ) : activeTrips.length === 0 ? (
            <div className="no-results">
              <img src="/ladakh.jpg" alt="No results" />
              <h3>No trips found</h3>
              <p>Try adjusting your search or filter.</p>
              <button className="btn-primary" onClick={() => { setSearch(''); setActiveCategory('All'); setSort(''); }}>
                Clear Filters
              </button>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab + activeCategory + search + sort}
                className="trips-grid-main"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
              >
                {activeTrips.map((trip) => <TripCard key={trip._id} trip={trip} />)}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
}
